import React, { useEffect, useState } from "react";
import { fetchMessagesPerDateAPI } from "../../../services/message";
import BarChart from "../../../components/BarChart";
import Loader from "../../../components/Loader";

const Messages = ({ startDate, endDate, selectedAssistant, msgType }) => {
  const [chartLabels, setChartLabels] = useState([]);
  const [allMessagesData, setAllMessagesData] = useState([]);
  const [sentMessagesData, setSentMessagesData] = useState([]);
  const [receivedMessagesData, setReceivedMessagesData] = useState([]);
  const [failedMessagesData, setFailedMessagesData] = useState([]);
  const [messagesCount, setMessagesCount] = useState({ all: 0, sent: 0, received: 0, failed: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMessagesPerDate = async (postData) => {
    setIsLoading(true);
    try {
      const responseData = await fetchMessagesPerDateAPI(postData);

      if (responseData.status === "success") {
        const { startDate, endDate } = postData;
        const dateMap = {};
        let current = new Date(startDate);
        const endDt = new Date(endDate);

        while (current <= endDt) {
          const iso = current.toISOString().split("T")[0];
          dateMap[iso] = { date: iso, sent: 0, delivered: 0, fail: 0, pending: 0, read: 0 };
          current.setDate(current.getDate() + 1);
        }

        responseData.data.forEach((item) => {
          if (dateMap[item.date]) {
            dateMap[item.date] = { ...dateMap[item.date], ...item };
          }
        });

        const filledData = Object.values(dateMap).sort((a, b) => new Date(a.date) - new Date(b.date));

        const labels = filledData.map((item) => item.date);
        const sentData = filledData.map((item) => item.sent || 0);
        const receivedData = filledData.map((item) => item.received || 0);
        const failData = filledData.map((item) => item.fail || 0);

        // All Messages: Sum of sent, delivered, fail, pending, and read
        const allMessagesData = filledData.map((item) => item.sent + item.received + item.fail || 0);

        const sentMessagesData = sentData;
        const receivedMessagesData = receivedData;
        const failedMessagesData = failData;

        setMessagesCount({ all: allMessagesData.reduce((total, current) => total + current, 0), sent: sentMessagesData.reduce((total, current) => total + current, 0), received: receivedMessagesData.reduce((total, current) => total + current, 0), failed: failedMessagesData.reduce((total, current) => total + current, 0) });

        setChartLabels(labels);
        setAllMessagesData(allMessagesData);
        setSentMessagesData(sentMessagesData);
        setReceivedMessagesData(receivedMessagesData);
        setFailedMessagesData(failedMessagesData);
      }
    } catch (error) {
      setError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }
    const startDateISO = startDate.toISOString().split("T")[0];
    const endDateISO = endDate.toISOString().split("T")[0];
    let postData = {
      startDate: startDateISO,
      endDate: endDateISO,
      assistant_id: selectedAssistant || "",
      msgType,
    };
    fetchMessagesPerDate(postData);
  }, [startDate, endDate, selectedAssistant, msgType]);

  if (isLoading) {
    return <Loader />;
  }
  if (error) {
    return <div>Error: {error?.message}</div>;
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-4 ">
      <div className="inline-block px-3 py-3 rounded-md border border-gray-800">
        <div className="relative py-3 text-left text-lg text-slate-400">
          All Messages<span className="absolute right-0 text-white text-[30px]">{messagesCount.all}</span>
        </div>
        {chartLabels && allMessagesData.length > 0 && <BarChart chartLabels={chartLabels} chartData={allMessagesData} />}
      </div>

      <div className="inline-block px-3 py-3 rounded-md border border-gray-800">
        <div className="relative py-3 text-left text-lg text-slate-400">
          Sent Messages<span className="absolute right-0 text-white text-[30px]">{messagesCount.sent}</span>
        </div>
        {chartLabels && sentMessagesData.length > 0 && <BarChart chartLabels={chartLabels} chartData={sentMessagesData} />}
      </div>

      <div className="inline-block px-3 py-3 rounded-md border border-gray-800">
        <div className="relative py-3 text-left text-lg text-slate-400">
          {msgType === 'Email' ? 'Opened Messages' : 'Received Messages'}<span className="absolute right-0 text-white text-[30px]">{messagesCount.received}</span>
        </div>
        {chartLabels && receivedMessagesData.length > 0 && <BarChart chartLabels={chartLabels} chartData={receivedMessagesData} />}
      </div>

      <div className="inline-block px-3 py-3 rounded-md border border-gray-800">
        <div className="relative py-3 text-left text-lg text-slate-400">
          {msgType === 'Email' ? 'Pending Messages' : 'Failed Messages'}<span className="absolute right-0 text-white text-[30px]">{messagesCount.failed}</span>
        </div>
        {chartLabels && failedMessagesData.length > 0 && <BarChart chartLabels={chartLabels} chartData={failedMessagesData} />}
      </div>
    </div>
  );
};

export default Messages;
